import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { ChefHat, Wine, Heart } from "lucide-react";

const TeamCard = ({ member, index, isInView }) => {
  const Icon = member.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.12 }}
      className="bg-white/80 backdrop-blur-sm border border-[#D8D0C5] rounded-sm overflow-hidden
                 shadow-sm hover:shadow-md transition-shadow duration-300 group"
      data-testid={`team-member-${index}`}
    >
      {/* Photo or placeholder */}
      {member.photo ? (
        <div className="w-full h-72 overflow-hidden">
          <img
            src={member.photo}
            alt={member.role}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            style={{ objectPosition: "center 25%" }}
            loading="lazy"
          />
        </div>
      ) : (
        <div
          className="w-full h-72 flex flex-col items-center justify-center"
          style={{ backgroundColor: `${member.color}08` }}
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110"
            style={{ backgroundColor: `${member.color}15` }}
          >
            <Icon size={32} style={{ color: member.color }} />
          </div>
          <p
            className="text-xs text-[#8C847F] italic"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.9rem" }}
          >
            {member.placeholder}
          </p>
        </div>
      )}

      {/* Info */}
      <div className="p-6">
        <p
          className="text-xs uppercase tracking-[0.2em] mb-2"
          style={{ color: member.color, fontFamily: "'Lato', sans-serif" }}
        >
          {member.role}
        </p>
        <h3
          className="text-xl md:text-2xl font-light text-[#2C2420] mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {member.name}
        </h3>
        <p
          className="text-sm text-[#5D544F] leading-relaxed"
          style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
        >
          {member.bio}
        </p>
      </div>
    </motion.div>
  );
};

export const TeamSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const teamMembers = [
    {
      role: t("team.role_chef"),
      icon: ChefHat,
      color: "#C1624D",
      photo: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/rgyd75uf_IMG_0435.jpeg",
      placeholder: t("team.photo_coming"),
      name: "Clément",
      bio: t("team.bio_chef"),
    },
    {
      role: t("team.role_patronne"),
      icon: Heart,
      color: "#A5C4D4",
      photo: "https://customer-assets-jt897jd0.emergentagent.net/job_poste-entrechaux/artifacts/h8o10aj8_IMG_0430.jpeg",
      placeholder: t("team.photo_coming"),
      name: "Christelle",
      bio: t("team.bio_patronne"),
    },
    {
      role: t("team.role_salle"),
      icon: Wine,
      color: "#8B6F4E",
      placeholder: t("team.photos_coming"),
      name: t("team.name_salle"),
      bio: t("team.bio_salle"),
    },
  ];

  return (
    <section
      id="equipe"
      data-testid="team-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
            style={{ fontFamily: "'Lato', sans-serif" }}
          >
            {t("team.label")}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="team-heading"
          >
            {t("team.heading")}
          </h2>
          <div className="ornament-divider max-w-xs mx-auto">
            <span
              className="text-[#C1624D] text-2xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              &bull;
            </span>
          </div>
          <p
            className="text-base text-[#8C847F] mt-4 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
          >
            {t("team.subtitle")}
          </p>
        </motion.div>

        {/* Team grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {teamMembers.map((member, i) => (
            <TeamCard
              key={member.role}
              member={member}
              index={i}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p
            className="text-sm text-[#8C847F] italic"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1rem" }}
          >
            {t("team.bios_coming")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
